import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Plus, 
  Trash2, 
  Users,
  Shield,
  User,
  Loader2
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialUser = {
  email: "",
  name: "",
  password: "",
  is_admin: false,
};

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialUser);
  const [saving, setSaving] = useState(false);
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Benutzer");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(initialUser);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API}/auth/users`, formData);
      toast.success("Benutzer erstellt");
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Möchten Sie diesen Benutzer wirklich löschen?")) return;

    try {
      await axios.delete(`${API}/auth/users/${userId}`);
      toast.success("Benutzer gelöscht");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-muted rounded animate-pulse w-48" />
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-12 bg-muted rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-[Manrope] tracking-tight">{t("users.title")}</h1>
          <p className="text-muted-foreground mt-1">
            {t("users.manageSystem")}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#E31E24] hover:bg-[#c91a1f]"
              data-testid="add-user-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("users.newUser")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="font-[Manrope]">{t("users.newUser")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">{t("users.name")} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Max Mustermann"
                  required
                  data-testid="user-name"
                />
              </div>
              <div>
                <Label htmlFor="email">{t("users.email")} *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="max@beispiel.de"
                  required
                  data-testid="user-email"
                />
              </div>
              <div>
                <Label htmlFor="password">{t("users.password")} *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="user-password"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t("users.minChars")}
                </p>
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label htmlFor="admin">{t("users.isAdmin")}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t("users.canManageUsers")}
                  </p>
                </div>
                <Switch
                  id="admin"
                  checked={formData.is_admin}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_admin: checked })}
                  data-testid="user-admin"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  {t("common.cancel")}
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#E31E24] hover:bg-[#c91a1f]"
                  disabled={saving}
                  data-testid="save-user-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t("users.creating")}
                    </>
                  ) : (
                    t("users.create")
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 md:gap-4">
        <Card className="card-hover" data-testid="stat-total-users">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-muted-foreground">{t("users.totalUsers")}</p>
                <p className="text-2xl md:text-3xl font-bold mt-1 md:mt-2">{users.length}</p>
              </div>
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-[#E31E24]/10 flex items-center justify-center">
                <Users className="h-5 w-5 md:h-6 md:w-6 text-[#E31E24]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover" data-testid="stat-admins">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-muted-foreground">{t("users.administrators")}</p>
                <p className="text-2xl md:text-3xl font-bold mt-1 md:mt-2">
                  {users.filter(u => u.is_admin).length}
                </p>
              </div>
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-[#09090b]/10 flex items-center justify-center">
                <Shield className="h-5 w-5 md:h-6 md:w-6 text-[#09090b]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {users.map((user) => (
          <Card key={user.id} className="card-hover" data-testid={`user-card-${user.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    <span className="font-semibold text-sm">
                      {user.name?.charAt(0)?.toUpperCase() || "U"}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{user.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                    <div className="mt-1">
                      {user.is_admin ? (
                        <Badge className="bg-[#E31E24]/10 text-[#E31E24] hover:bg-[#E31E24]/20 text-xs">
                          <Shield className="h-3 w-3 mr-1" />
                          Admin
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          <User className="h-3 w-3 mr-1" />
                          Benutzer
                        </Badge>
                      )}
                      {user.id === currentUser?.id && (
                        <span className="text-xs text-muted-foreground ml-2">({t("users.you")})</span>
                      )}
                    </div>
                  </div>
                </div>
                {user.id !== currentUser?.id && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(user.id)}
                    className="text-destructive hover:text-destructive flex-shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Desktop Table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("users.user")}</TableHead>
                <TableHead>{t("users.email")}</TableHead>
                <TableHead className="text-center">{t("users.role")}</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className="table-row-hover" data-testid={`user-row-${user.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                        <span className="font-semibold text-sm">
                          {user.name?.charAt(0)?.toUpperCase() || "U"}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{user.name}</p>
                        {user.id === currentUser?.id && (
                          <p className="text-xs text-muted-foreground">Sie</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{user.email}</TableCell>
                  <TableCell className="text-center">
                    {user.is_admin ? (
                      <Badge className="bg-[#E31E24]/10 text-[#E31E24] hover:bg-[#E31E24]/20">
                        <Shield className="h-3 w-3 mr-1" />
                        Admin
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <User className="h-3 w-3 mr-1" />
                        Benutzer
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.id !== currentUser?.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(user.id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
